// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { viewStyles } from "@pace/Overrides/Widgets/ProductDetails/ProductDetailsView";
import { DivWrapper } from "@pace/XDL/constants";
import { color, hideElementOnPrint, xScreen } from "@pace/XDL/Utilities";
import { css } from "styled-components";

viewStyles.pageContainer = {
    css: css`
        display: flex;
        flex-direction: column;
        overflow: hidden;
        &&&& ${DivWrapper} {
            height: auto;
        }
        div:has(> ${DivWrapper}:empty) {
            display: none;
        }
        @media print {
            display: block;
        }
    `,
};

viewStyles.columnsContainer = {
    css: css`
        display: grid;
        grid-template-columns: 1fr 319px;
        gap: 22px;
        ${xScreen("lte", 1400)} {
            grid-template-columns: minmax(300px,auto) 319px;
        }
        ${xScreen("lte", "md")} {
            display: flex;
            flex-wrap: wrap;
        }
        @media print {
            display: block;
        }
    `,
};

viewStyles.twoColumnsSection = {
    css: css`
        display: grid;
        grid-template-columns: minmax(430px, auto) minmax(543px, auto);
        gap: 22px;
        width: 100%;
        ${xScreen("lte", 1400)} {
            grid-template-columns: minmax(320px, auto) minmax(320px, auto);
        }
        ${xScreen("lte", "md")} {
            display: flex;
            flex-wrap: wrap;
            flex-direction: column;
            gap: 24px;
        }
        ${xScreen("gte", "xl")} {
            grid-template-columns: minmax(508px, auto) minmax(640px, auto);
        }
        @media print {
            display: block;
        }
    `,
};

viewStyles.imageDescriptionActionsContainer = {
    css: css`
        ${xScreen("lte", "md")} {
            display: grid;
            grid-template-columns: minmax(0, auto) 360px;
            gap: 22px;
            justify-content: center;
        }
        ${xScreen("lte", "sm")} {
            display: flex;
            flex-wrap: wrap;
        }
    `,
};

viewStyles.infoDescriptionSection = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
    `,
};

viewStyles.divider = {
    css: css`
        border-bottom: 1px solid ${color("common.ui12")};
        margin-block: 12px;
    `,
};

viewStyles.leftColumn = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 52px;
        width: 100%;
        @media print {
            display: block;
        }
    `,
};

viewStyles.rightColumn = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 32px;
        width: 317px;
        ${xScreen("lte", "sm")} {
            width: auto;
        }
        @media print {
            display: block;
            > div {
                margin-bottom: 12px;
            }
        }
    `,
};

viewStyles.productNumbersWrapper = {
    css: css`
        display: flex;
        gap: 8px;
        align-items: baseline;
        flex-wrap: wrap;
    `,
};

viewStyles.stickyWrapper = {
    css: css`
        position: fixed;
        left: 0;
        background: ${color("common.white")};
        width: 100%;
        z-index: 1300;
        transition: all 300ms ease-out;
        opacity: 1;
        min-height: 107px;
        height: auto;
        border-bottom: 1px solid ${color("common.border")};
        overflow: visible;

        &.stickyVisible {
            top: 0;
        }

        &.stickyHidden {
            top: -200px;
            &,
            & > * {
                max-height: 0;
                overflow: hidden;
            }
        }

        &.editingMode {
            position: unset;
        }

        ${xScreen("lte", "sm")} {
            display: none;
        }

        ${hideElementOnPrint}
    `,
};

viewStyles.stickyPage = {
    css: css`
        &&&& {
            padding: 8px 16px;
        }
    `,
};

viewStyles.videosAndReplacementContainer = {
    css: css`
        ${DivWrapper} {
            display: flex;
            gap: 22px;
            margin-bottom: 32px;
        }
        ${hideElementOnPrint}
    `,
};

viewStyles.bottomSection = {
    css: css`
        margin-top: 16px;
        ${hideElementOnPrint}
    `,
};

viewStyles.leftColumnGridItem = {
    css: css`
        @media print {
            display: block;
        }
    `,
};

viewStyles.rightColumnGridItem = {
    css: css`
        @media print {
            display: block;
        }
    `,
};

viewStyles.extraElementsContainer = {
    css: css`
        @media print {
            display: none;
        }
    `,
};